import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { UserRound, Users, Check, Loader2, CalendarDays, Video, ArrowRight } from "lucide-react";
import { toast } from "sonner";
import { useLang } from "../context/LanguageContext";
import { useAuth } from "../context/AuthContext";
import { Footer } from "../components/Footer";
import api, { formatApiError } from "../lib/api";
import useSeo from "../hooks/useSeo";

const STATUS_TONE = { pending_payment: "bg-[#FEF3C7] text-[#92400E]", new: "bg-[#EEF4FF] text-[#1D4ED8]", scheduled: "bg-[#DCFCE7] text-[#166534]", completed: "bg-[#E2E8F0] text-[#334155]" };

export default function Mentorship() {
  const { t, lang } = useLang();
  const { user } = useAuth();
  const [mine, setMine] = useState([]);

  useSeo({
    lang,
    title: lang === "en" ? "1:1 Sports Science Mentorship" : "Mentorship Αθλητικής Επιστήμης 1:1",
    description:
      lang === "en"
        ? "Book 1:1 or group mentorship with SportScience Academy experts: programming, physiology, nutrition and career guidance."
        : "Κλείσε mentorship 1:1 ή ομαδικό με τους ειδικούς της SportScience Academy: προγραμματισμός, φυσιολογία, διατροφή και καθοδήγηση καριέρας.",
  });
  const [params] = useSearchParams();
  const [form, setForm] = useState({ kind: params.get("kind") === "group" ? "group" : "one_on_one", topic: params.get("topic") || "", goal: "", availability: "", message: "" });
  const [sending, setSending] = useState(false);

  const reload = () => user && api.get("/mentorship/mine").then((r) => setMine(r.data)).catch(() => {});

  useEffect(() => {
    reload();
  }, [user]); // eslint-disable-line

  const submit = async (e) => {
    e.preventDefault();
    if (!user) { window.location.href = "/login"; return; }
    setSending(true);
    try {
      await api.post("/mentorship/request", { ...form, origin_url: window.location.origin });
      toast.success(t("mentorship.sent"));
      setForm({ kind: form.kind, topic: "", goal: "", availability: "", message: "" });
      reload();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSending(false);
    }
  };

  const kinds = [
    { id: "one_on_one", icon: UserRound },
    { id: "group", icon: Users },
  ];

  return (
    <>
      <div className="max-w-6xl mx-auto px-5 sm:px-6 pt-14 pb-20" data-testid="mentorship-page">
        <span className="badge-lime">{t("mentorship.badge")}</span>
        <h1 className="font-heading font-black text-4xl sm:text-5xl lg:text-6xl mt-5 text-[#0B1B3A]" data-testid="mentorship-title">{t("mentorship.title")}</h1>
        <p className="text-base text-[#475569] mt-4 max-w-2xl">{t("mentorship.subtitle")}</p>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-10" data-testid="mentorship-kinds">
          {kinds.map((k, i) => (
            <motion.button
              key={k.id}
              type="button"
              data-testid={`mentorship-kind-${k.id}`}
              initial={{ opacity: 0, y: 18 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: i * 0.08 }}
              onClick={() => setForm({ ...form, kind: k.id })}
              className={`card-dark p-7 text-left transition-all duration-200 border-2 ${form.kind === k.id ? "border-[#1D4ED8] shadow-lg" : "border-transparent hover:border-[#BFDBFE]"}`}
            >
              <span className="w-12 h-12 rounded-2xl bg-[#1D4ED8] flex items-center justify-center"><k.icon className="w-6 h-6 text-white" /></span>
              <div className="flex items-start justify-between gap-4 mt-5">
                <h2 className="font-heading font-black text-lg md:text-lg text-[#0B1B3A]">{t(`mentorship.${k.id}.title`)}</h2>
                {form.kind === k.id && <Check className="w-6 h-6 text-[#1D4ED8]" />}
              </div>
              <p className="text-sm text-[#475569] mt-3">{t(`mentorship.${k.id}.desc`)}</p>
              <ul className="mt-5 space-y-2">
                {[1, 2, 3].map((n) => (
                  <li key={n} className="flex items-start gap-2 text-sm text-[#1E293B] font-semibold"><Check className="w-4 h-4 text-[#1D4ED8] mt-0.5 shrink-0" /> {t(`mentorship.${k.id}.p${n}`)}</li>
                ))}
              </ul>
            </motion.button>
          ))}
        </div>

        <form onSubmit={submit} className="card-dark p-7 sm:p-9 mt-10 space-y-5" data-testid="mentorship-form">
          <h3 className="font-heading font-black text-xl text-[#0B1B3A]">{t("mentorship.formTitle")} — {t(`mentorship.${form.kind}.title`)}</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            <div>
              <label className="text-xs font-bold uppercase tracking-[0.16em] text-[#64748B]">{t("mentorship.topic")}</label>
              <input data-testid="mentorship-topic-input" required value={form.topic} onChange={(e) => setForm({ ...form, topic: e.target.value })} className="input-flat mt-2" placeholder={t("mentorship.topicPh")} />
            </div>
            <div>
              <label className="text-xs font-bold uppercase tracking-[0.16em] text-[#64748B]">{t("mentorship.availability")}</label>
              <input data-testid="mentorship-availability-input" value={form.availability} onChange={(e) => setForm({ ...form, availability: e.target.value })} className="input-flat mt-2" placeholder={t("mentorship.availabilityPh")} />
            </div>
          </div>
          <div>
            <label className="text-xs font-bold uppercase tracking-[0.16em] text-[#64748B]">{t("mentorship.goal")}</label>
            <input data-testid="mentorship-goal-input" value={form.goal} onChange={(e) => setForm({ ...form, goal: e.target.value })} className="input-flat mt-2" placeholder={t("mentorship.goalPh")} />
          </div>
          <div>
            <label className="text-xs font-bold uppercase tracking-[0.16em] text-[#64748B]">{t("mentorship.message")}</label>
            <textarea data-testid="mentorship-message-input" rows={4} value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} className="input-flat mt-2" />
          </div>
          <button data-testid="mentorship-submit" disabled={sending} className="btn-blue disabled:opacity-60">
            {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <ArrowRight className="w-4 h-4" />}
            {user ? t("mentorship.send") : t("mentorship.loginFirst")}
          </button>
        </form>

        {user && mine.length > 0 && (
          <div className="mt-14" data-testid="mentorship-mine">
            <h2 className="font-heading font-black text-2xl sm:text-3xl text-[#0B1B3A]">{t("mentorship.mine")}</h2>
            <div className="mt-6 space-y-4">
              {mine.map((r) => (
                <div key={r.id} className="card-dark p-6 flex flex-wrap items-center gap-4" data-testid={`mentorship-row-${r.id}`}>
                  <div className="flex-1 min-w-[220px]">
                    <p className="font-bold text-[#0B1B3A]">{t(`mentorship.${r.kind}.title`)} · {r.topic}</p>
                    <p className="text-xs text-[#64748B] mt-1">{new Date(r.created_at).toLocaleDateString("el-GR")}{r.scheduled_at ? ` · ${r.scheduled_at}` : ""}</p>
                  </div>
                  <span className={`px-3 py-1 rounded-full text-xs font-bold ${STATUS_TONE[r.status] || STATUS_TONE.new}`} data-testid={`mentorship-status-${r.id}`}>{t(`mentorship.status.${r.status}`)}</span>
                  {r.scheduled_at && <span className="inline-flex items-center gap-1.5 text-sm font-semibold text-[#0B1B3A]"><CalendarDays className="w-4 h-4 text-[#1D4ED8]" /> {r.scheduled_at}</span>}
                  {r.meeting_url && <a href={r.meeting_url} target="_blank" rel="noreferrer" className="btn-blue !px-4 !py-2 text-sm" data-testid={`mentorship-join-${r.id}`}><Video className="w-4 h-4" /> {t("mentorship.join")}</a>}
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
      <Footer />
    </>
  );
}
